import { empty, intval } from '../../../php-compat';
import { readItemField } from '../../../compile/item-field-reader';
import { FromBaseStageMissing } from '../../../compile/from-base-context';
import type { FromBaseRow as Row } from '../../../compile/from-base-context';
import { normalizeOrbQueue } from '../../../ability/orbs/orb-queue';
import { phpJsonFloat } from '../../../compile/php-decimal-values';
import { compileCreatureApsTotal } from '../../../compile/creature-meta-defaults';

const columns = ['encounter_creature_created_by_id', 'encounter_creature_updated_by_id',
    'encounter_id', 'combat_id', 'encounter_creature_id', 'encounter_creature_name',
    'encounter_creature_flavor_text', 'encounter_creature_personality', 'encounter_creature_quirks',
    'encounter_creature_motivations', 'encounter_creature_secrets', 'encounter_creature_tactics',
    'encounter_creature_gm_notes', 'item_id', 'item_type', 'encounter_creature_monster_template_id',
    'team', 'controller_encounter_creature_id', 'summon_type', 'creature_number', 'creature_initiative',
    'v_current', 'v_max', 'v_temp', 'v_max_temp', 'spell_list_prepared', 'aps', 'aps_total', 'weapon_attack',
    'head_attack', 'reaction', 'mana', 'luck', 'spirit', 'hunger_meter_current', 'thirst_meter_current',
    'movement_pct', 'movement_rev', 'preferred_speed_type', 'encounter_creature_presentation_pic_id',
    'encounter_creature_thumbnail_pic_id', 'gender', 'body_appearance', 'appearance', 'orb_queue',
    'merchant_favor', 'voice_id', 'lair_dc', 'encounter_weather', 'encounter_environment',
    'encounter_tempurature', 'encounter_wind', 'count_caster_summons'] as const;
const anchors = new Set(['encounter_id', 'encounter_creature_id', 'item_id', 'item_type']);

/** PHP: build_encounter_creature_arr
 * (includes/functions/db/encounter_creatures/build_encounter_creature_arr.php:4),
 * pure source prefix before vitality, position and presentation. */
export function constructEncounterCreatureSourceFromBase(source: Row,
    summons?: readonly Row[]): Row {
    const field = (name: string) => readItemField(source, name);
    const row: Row = {};
    for (const name of columns) {
        let value = field(name);
        if (value != null && value !== '' && ['appearance', 'body_appearance'].includes(name))
            throw new FromBaseStageMissing('classification-template-swarm-media', `normalizer:${name}`);
        if (name === 'orb_queue' && value != null && value !== '') value = normalizeOrbQueue(value);
        // A nullable lair DC must distinguish an explicit zero from the omitted NULL fallback.
        const keepZero = (name === 'lair_dc' || name === 'mana' && field('item_type') !== 'player_character')
            && (value === 0 || value === '0');
        if (anchors.has(name) || keepZero || value != null && value !== '' && value !== 0 && value !== '0') row[name] = value;
    }
    if (!empty(readItemField(row, 'count_caster_summons'))) {
        if (!summons) throw new FromBaseStageMissing('definition-source-initialization', 'caster-summons-select');
        row.caster_summons = structuredClone(summons);
    }
    for (const name of ['encounter_creature_private', 'encounter_creature_deleted', 'encounter_creature_dead'])
        if (!empty(field(name))) row[name] = field(name);
    const total = compileCreatureApsTotal(String(readItemField(row, 'item_type') ?? ''), readItemField(row, 'aps_total'));
    const aps = Math.min(127, Math.max(0, Number(intval(readItemField(row, 'aps') ?? 0))));
    row.aps_total = total;
    row.aps = aps;
    if (total > aps) row.aps_used = total - aps;
    const spirit = phpJsonFloat(readItemField(row, 'spirit'));
    const manaPercent = spirit !== 0 ? Math.floor(phpJsonFloat(readItemField(row, 'mana')) / spirit * 100) : 0;
    if (manaPercent !== 0) row.mana_percent = manaPercent;
    return row;
}
